import React from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  TextField,
  Grid,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { FormComponentProps, BasicInfoData } from '../../types/forms';

const contactOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'SMS', value: 'sms' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Signal', value: 'signal' }
];

const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  middleName: yup.string(),
  lastName: yup.string().required('Last name is required'),
  dateOfBirth: yup.string().required('Date of birth is required'),
  ssn: yup.string()
    .matches(/^[0-9]{3}-[0-9]{2}-[0-9]{4}$/, 'SSN must be in XXX-XX-XXXX format')
    .required('SSN is required'),
  maritalStatus: yup.string().required('Marital status is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  mobile: yup.string().required('Mobile number is required'),
  workPhone: yup.string(),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zipCode: yup.string()
    .matches(/^[0-9]{5}(-[0-9]{4})?$/, 'ZIP code must be in XXXXX or XXXXX-XXXX format')
    .required('ZIP code is required'),
  preferredContact: yup.array().of(yup.string()).min(1, 'Select at least one contact method'),
  dlState: yup.string(),
  dlNumber: yup.string(),
  dlExpiry: yup.string()
}) as yup.ObjectSchema<BasicInfoData>;

const states = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

// Helper to format SSN as XXX-XX-XXXX
function formatSSN(value: string) {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
}

// Helper to format phone as (XXX) XXX-XXXX
function formatPhone(value: string) {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}



const BasicInfo: React.FC<FormComponentProps<BasicInfoData>> = ({ data, onNext, onDataChange }) => {
  const { control, handleSubmit, formState: { errors }, watch, reset } = useForm<BasicInfoData>({
    resolver: yupResolver(schema),
    defaultValues: data || {
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      ssn: '',
      maritalStatus: '',
      email: '',
      mobile: '',
      workPhone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      preferredContact: [],
      dlState: '',
      dlNumber: '',
      dlExpiry: ''
    }
  });

  // Reset form when data changes (e.g., after loading saved progress)
  React.useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  // Watch all form fields and call onDataChange when they change
  const watchedValues = watch();
  React.useEffect(() => {
    if (onDataChange) {
      onDataChange(watchedValues);
    }
  }, [watchedValues, onDataChange]);

  const onSubmit: SubmitHandler<BasicInfoData> = (formData) => {
    onNext(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ px: 5, py: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 400, mb: 1 }}>
        Basic Client Information
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
        Let's start with your basic information to establish your identity and contact details.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>First Name</span> <span style={{ color: '#dc3545' }}>*</span></>}
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="middleName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Middle Name"
                fullWidth
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>Last Name</span> <span style={{ color: '#dc3545' }}>*</span></>}
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>Date of Birth</span> <span style={{ color: '#dc3545' }}>*</span></>}
                type="date"
                fullWidth
                required
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth?.message}
                InputLabelProps={{ shrink: true }}
                placeholder="mm/dd/yyyy"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="ssn"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>Social Security Number (SSN) or ITIN</span> <span style={{ color: '#dc3545' }}>*</span></>}
                placeholder="XXX-XX-XXXX"
                fullWidth
                required
                error={!!errors.ssn}
                helperText={errors.ssn?.message}
                inputProps={{ maxLength: 11 }}
                onChange={e => field.onChange(formatSSN(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="maritalStatus"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth required error={!!errors.maritalStatus}>
                <InputLabel shrink={true}>
                  <span>Marital Status <span style={{ color: '#dc3545' }}>*</span></span>
                </InputLabel>
                <Select {...field} label="Marital Status" key={`maritalStatus-${field.value}`}>
                  <MenuItem value="">Select Status</MenuItem>
                  <MenuItem value="single">Single</MenuItem>
                  <MenuItem value="marriedJoint">Married Filing Jointly</MenuItem>
                  <MenuItem value="marriedSeparate">Married Filing Separately</MenuItem>
                  <MenuItem value="headOfHousehold">Head of Household</MenuItem>
                  <MenuItem value="widow">Qualifying Widow(er)</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>Email Address</span> <span style={{ color: '#dc3545' }}>*</span></>}
                type="email"
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email?.message}
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="mobile"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>Mobile Number</span> <span style={{ color: '#dc3545' }}>*</span></>}
                fullWidth
                required
                error={!!errors.mobile}
                helperText={errors.mobile?.message}
                placeholder="(XXX) XXX-XXXX"
                inputProps={{ maxLength: 14 }}
                onChange={e => field.onChange(formatPhone(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="workPhone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Work Phone Number"
                fullWidth
                placeholder="(XXX) XXX-XXXX"
                inputProps={{ maxLength: 14 }}
                onChange={e => field.onChange(formatPhone(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>Street Address</span> <span style={{ color: '#dc3545' }}>*</span></>}
                fullWidth
                required
                error={!!errors.address}
                helperText={errors.address?.message}
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>City</span> <span style={{ color: '#dc3545' }}>*</span></>}
                fullWidth
                required
                error={!!errors.city}
                helperText={errors.city?.message}
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth required error={!!errors.state}>
                <InputLabel shrink={true}>
                  <span>State <span style={{ color: '#dc3545' }}>*</span></span>
                </InputLabel>
                <Select {...field} label="State" key={`state-${field.value}`}>
                  <MenuItem value="">Select State</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.code} value={state.code}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.state && (
                  <FormHelperText>{errors.state.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="zipCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={<><span>ZIP Code</span> <span style={{ color: '#dc3545' }}>*</span></>}
                fullWidth
                required
                error={!!errors.zipCode}
                helperText={errors.zipCode?.message}
                placeholder="XXXXX or XXXXX-XXXX"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset" error={!!errors.preferredContact}>
            <Typography variant="subtitle1" gutterBottom>
              Preferred Contact Methods <span style={{ color: '#dc3545' }}>*</span>
            </Typography>
            <FormGroup row>
              {contactOptions.map((option) => (
                <Controller
                  key={option.value}
                  name="preferredContact"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value?.includes(option.value)}
                          onChange={(e) => {
                            const value = e.target.checked
                              ? [...(field.value || []), option.value]
                              : (field.value || []).filter((v) => v !== option.value);
                            field.onChange(value);
                          }}
                        />
                      }
                      label={option.label}
                    />
                  )}
                />
              ))}
            </FormGroup>
            {errors.preferredContact && (
              <FormHelperText>{errors.preferredContact.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Driver's License Information (Optional)
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="dlState"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel shrink={true}>State</InputLabel>
                <Select {...field} label="State" key={`dlState-${field.value}`}>
                  <MenuItem value="">Select State</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.code} value={state.code}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="dlNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="License Number"
                fullWidth
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="dlExpiry"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Expiration Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder="mm/dd/yyyy"
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfo; 